import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { contactListService } from "@/lib/api-services/contact-list-service";
import { useAuthStore } from "@/store/authStore";
import type { ContactList } from "@/types/email";
import type { WizardData } from "@/pages/campaigns/CampaignWizard";

interface StepRecipientsProps {
  selectedListIds: string[];
  estimatedRecipients: number;
  trackOpens: boolean;
  trackClicks: boolean;
  onChange: (partial: Partial<WizardData>) => void;
  onBack: () => void;
  onNext: () => void;
}

const LIST_TYPE_COLORS: Record<string, string> = {
  Static: "bg-muted text-muted-foreground",
  Dynamic: "bg-blue-500/10 text-blue-500",
  Segment: "bg-purple-500/10 text-purple-500",
};

export function StepRecipients({
  selectedListIds,
  estimatedRecipients,
  trackOpens,
  trackClicks,
  onChange,
  onBack,
  onNext,
}: StepRecipientsProps) {
  const user = useAuthStore((s) => s.user);
  const [lists, setLists] = useState<ContactList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const accountId = user?.currentAccountId ?? user?.ownedAccountId;
    if (!accountId) return;
    contactListService
      .getContactLists(accountId, { pageSize: 100, isActive: true })
      .then((result) => {
        setLists(result.items);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load contact lists");
      })
      .finally(() => setIsLoading(false));
  }, [user]);

  const toggleList = (list: ContactList) => {
    const isSelected = selectedListIds.includes(list.id);
    const newIds = isSelected
      ? selectedListIds.filter((id) => id !== list.id)
      : [...selectedListIds, list.id];
    const newTotal = lists
      .filter((l) => newIds.includes(l.id))
      .reduce((sum, l) => sum + l.contactCount, 0);
    onChange({ selectedListIds: newIds, estimatedRecipients: newTotal });
  };

  const canProceed = selectedListIds.length > 0;

  return (
    <div className="lw-step-recipients">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Choose recipients</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Select the contact lists to send this campaign to.
        </p>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
            Loading lists…
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive mb-4">
          {error}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && lists.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center mb-4">
          <p className="text-sm text-muted-foreground">No contact lists yet.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Create contact lists in Audience → Lists before sending campaigns.
          </p>
        </div>
      )}

      {/* List picker */}
      {!isLoading && lists.length > 0 && (
        <div className="space-y-2 mb-6">
          {lists.map((list) => {
            const isSelected = selectedListIds.includes(list.id);
            return (
              <button
                key={list.id}
                onClick={() => toggleList(list)}
                className={`lw-list-picker-item w-full flex items-center justify-between rounded-lg border px-4 py-3 text-left transition-all ${
                  isSelected
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                      isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"
                    }`}
                  >
                    {isSelected && (
                      <svg className="h-2.5 w-2.5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{list.name}</p>
                    {list.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1">{list.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${LIST_TYPE_COLORS[list.listType] ?? "bg-muted text-muted-foreground"}`}>
                    {list.listType}
                  </span>
                  <span className="text-xs text-muted-foreground">{list.contactCount.toLocaleString()}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Estimated recipient count */}
      {selectedListIds.length > 0 && (
        <div className="mb-5 rounded-md bg-muted/40 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Estimated recipients</span>
          <span className="text-sm font-semibold text-foreground">{estimatedRecipients.toLocaleString()}</span>
        </div>
      )}

      {/* Tracking options */}
      <div className="mb-6 space-y-3">
        <h3 className="text-sm font-medium text-foreground">Tracking</h3>
        <label className="flex items-center gap-3 cursor-pointer group">
          <div
            onClick={() => onChange({ trackOpens: !trackOpens })}
            className={`relative h-5 w-9 rounded-full transition-colors ${
              trackOpens ? "bg-primary" : "bg-muted"
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                trackOpens ? "translate-x-4" : "translate-x-0.5"
              }`}
            />
          </div>
          <span className="text-sm text-foreground">Track email opens</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            onClick={() => onChange({ trackClicks: !trackClicks })}
            className={`relative h-5 w-9 rounded-full transition-colors ${
              trackClicks ? "bg-primary" : "bg-muted"
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                trackClicks ? "translate-x-4" : "translate-x-0.5"
              }`}
            />
          </div>
          <span className="text-sm text-foreground">Track link clicks</span>
        </label>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <Button onClick={onNext} disabled={!canProceed}>
          Next: Review
        </Button>
      </div>
    </div>
  );
}
